import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChartPieIcon,
  PencilSquareIcon,
  ArrowDownOnSquareStackIcon,
  AdjustmentsHorizontalIcon,
  Cog6ToothIcon,
  ShoppingCartIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  ShareIcon,
  BuildingOffice2Icon,
  BanknotesIcon,
  CalculatorIcon,
  UsersIcon,
  Bars3Icon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useAppContext } from '../../context/AppContext';
import { isRoleAdmin } from '../../utils/permissions';
import CompanyLogo from './CompanyLogo';
import MantleBrand from './MantleBrand';

// Each item declares the module that unlocks it (Settings > Role Data Access).
// `always` items show for every role. Roles with MOD_0_ADMIN see every
// operational module, EXCEPT items flagged `sensitive` or `adminOnly`.
//
// `sensitive` modules (e.g. People — salaries and pay cost) are deliberately
// excluded from the admin blanket: they must be granted explicitly per role,
// even to administrators, via Settings > Role Data Access. This keeps payroll
// data locked down by default rather than visible to anyone with admin rights.
//
// `adminOnly` items (Settings, Imports, Budget Setup) configure the tenant
// itself, so they need the ACTING role to be administrative — the isDangerous
// attribute on the role, not a module grant. Holding MOD_0_ADMIN is no longer
// enough on its own: that module is a data-access grant, and it was letting a
// non-admin role that had been given it reach tenant configuration.
const NAV_ITEMS = [
  // Reporting is a real module now, not the static dashboard it used to be, so
  // it is gated like one. Access INSIDE it is finer still: role_report_access
  // grants a role individual SECTIONS, so holding the module gets you the
  // landing page and only the sections you were given.
  { name: 'Reporting', path: '/reports', icon: ChartPieIcon, module: 'MOD_10_REPORT' },
  // Insights sits next to Reporting because it answers the same kind of question
  // by a different route: a report is a layout somebody designed, this is a
  // question nobody anticipated. Gated like a normal module - what the AI is
  // allowed to see is narrowed FURTHER inside it, per acting role, because the
  // catalogue is filtered by role_module_access (utils/aiCatalogue.js canSee).
  { name: 'Insights', path: '/insights', icon: SparklesIcon, module: 'MOD_11_INSIGHT' },
  { name: 'Opex Entry', path: '/opex', icon: PencilSquareIcon, module: 'MOD_1_OPEX' },
  { name: 'Purchase', path: '/purchase', icon: ShoppingCartIcon, module: 'MOD_2_SUPPLY' },
  { name: 'Manufacturing', path: '/manufacturing', icon: WrenchScrewdriverIcon, module: 'MOD_3_MAKE' },
  { name: 'Distribution', path: '/distribution', icon: TruckIcon, module: 'MOD_4_MOVE' },
  // Planning is the network itself — which sites feed which, and how much moves
  // between them. Its own module because the audience is different: planners
  // decide what moves, the Distribution module prices it.
  { name: 'Planning', path: '/planning', icon: ShareIcon, module: 'MOD_9_PLAN' },
  { name: 'Capital', path: '/capital', icon: BuildingOffice2Icon, module: 'MOD_5_CAPEX' },
  { name: 'Revenue', path: '/revenue', icon: BanknotesIcon, module: 'MOD_6_PROFIT' },
  { name: 'People', path: '/people', icon: UsersIcon, module: 'MOD_8_PEOPLE', sensitive: true },
  // Finance sits after the operating modules because it consolidates them. The
  // only module that is cross-site by construction: it ignores the site
  // selector rather than honouring it, since "has everyone submitted?" is not
  // a question about one depot.
  { name: 'Finance', path: '/finance', icon: CalculatorIcon, module: 'MOD_7_FIN' },
  // Renamed from "Imports": the section now maintains master data by hand as
  // well as by file, and importing is only one way in.
  { name: 'Master Data', path: '/imports', icon: ArrowDownOnSquareStackIcon, adminOnly: true },
  { name: 'Budget Setup', path: '/budget-setup', icon: AdjustmentsHorizontalIcon, adminOnly: true },
  { name: 'Settings', path: '/settings', icon: Cog6ToothIcon, adminOnly: true },
];

const Sidebar = ({ collapsed = false, onToggle }) => {
  const location = useLocation();
  const { roleModules, selectedRole } = useAppContext();

  // A role holding MOD_0_ADMIN sees every OPERATIONAL screen. Two carve-outs
  // sit outside that blanket, and the order of the checks below is what
  // enforces them — both are tested before `seesAll` is ever consulted.
  const seesAll = roleModules.includes('MOD_0_ADMIN');
  // The role being acted as, not merely a role the user holds somewhere.
  const roleIsAdmin = isRoleAdmin(selectedRole);
  const canSee = (item) => {
    // 1. Sensitive modules (People — salaries, pay cost) are never covered by
    //    the admin blanket. The ACTING role must hold the module explicitly,
    //    administrator or not. No `seesAll`, no `isAdmin`, no exceptions.
    if (item.sensitive) return !!item.module && roleModules.includes(item.module);
    // 2. Tenant configuration needs an administrative role. A module grant is
    //    data access and does not confer it.
    if (item.adminOnly) return roleIsAdmin;
    if (item.always || seesAll) return true;
    return !!item.module && roleModules.includes(item.module);
  };

  const navItems = NAV_ITEMS.filter(canSee);

  return (
    <div
      className={`flex flex-col bg-[#040404] text-white border-r border-black h-screen shrink-0 transition-all duration-200 ease-in-out ${collapsed ? 'w-16' : 'w-45'
        }`}
    >

      {/* Brand / company logo + hamburger toggle */}
      <div
        className={`flex items-center h-[60px] bg-[#fdfdfd] ${collapsed ? 'justify-center px-0' : 'justify-between px-3'
          }`}
      >
        {!collapsed && <CompanyLogo />}
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="p-1.5 rounded-md text-slate-700 hover:bg-slate-200 transition-colors"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 py-5 overflow-y-auto">
        <ul className="list-none p-0 m-0">
          {navItems.map((item) => {
            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + '/');
            const ItemIcon = item.icon;

            return (
              <li key={item.name}>
                <Link
                  to={item.path}
                  title={collapsed ? item.name : undefined}
                  className={`block py-3 border-l-4 transition-all duration-200 ease-in-out ${collapsed ? 'px-0' : 'px-4'
                    } ${isActive
                      ? 'text-[#686666] bg-[#334155] border-[#e9eff1]'
                      : 'text-[#cbd5e1] bg-transparent border-transparent hover:bg-[#334155]/40 hover:text-white'
                    }`}
                >
                  <span className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
                    <ItemIcon className={`h-5 w-5 shrink-0 ${isActive ? 'text-[#686666]' : 'text-[#cbd5e1]'}`} />
                    {!collapsed && <span className="truncate">{item.name}</span>}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Product brand footer (B) — "Powered by Mantle" on the dark rail */}
      <div
        className={`border-t border-[#1f1f1f] ${collapsed ? 'flex justify-center py-3' : 'px-4 py-3'
          }`}
      >
        <MantleBrand heightClass={collapsed ? 'h-6' : 'h-7'} poweredBy={!collapsed} onDark />
      </div>
    </div>
  );
};

export default Sidebar;
